import { memo } from "react";

const TIMELINE = [
  {
    year: "1980s",
    place: "Taichung, Taiwan",
    title: "Born in a teahouse",
    body:
      "Iced tea was losing steam against glossy Western coffee chains. In a quiet Taichung teahouse, someone dropped chewy tapioca pearls into a cold milk tea — and people showed up the next day just to drink it again.",
  },
  {
    year: "1988",
    place: "The bubble argument",
    title: "Two teahouses, one legend",
    body:
      "Chun Shui Tang says their product development manager poured tapioca pudding into her iced tea during a staff meeting in 1988. Hanlin Tea Room says they did it first, with white pearls. The debate (lovingly) never ended — and actually went to court. Both lost.",
  },
  {
    year: "1990s",
    place: "Crosses the Pacific",
    title: "Arrives in Chinatowns",
    body:
      "Bubble tea hopscotched across Asia through the '90s, then reached Los Angeles and Vancouver — first in Chinese-American neighborhoods, then in strip-mall cafés with glossy laminated menus and oversized Garfield plushies.",
  },
  {
    year: "2010s",
    place: "The cult era",
    title: "Instagram notices",
    body:
      "Brown sugar swirls, cheese foam, and neon-lit storefronts turned boba from drink into aesthetic. Late-night chains started opening in college towns. First-time drinkers learned the rule: always chew.",
  },
  {
    year: "2020s",
    place: "Global obsession",
    title: "A multi-billion-dollar sip",
    body:
      "Today the global bubble-tea industry is worth tens of billions and still accelerating. The fat straw is its own symbol now — recognizable in every city on earth, from Seoul to Stockholm to… Sun Valley.",
  },
  {
    year: "Today",
    place: "Idaho's turn",
    title: "The Boba Place",
    body:
      "We brought it home: small-batch, hand-cooked pearls, slow-steeped teas, and a mountain-town vibe. Same decades-old magic, now served with a straw wide enough to really deliver the goods.",
  },
];

function BobaHistory() {
  return (
    <section
      className="relative overflow-hidden"
      data-testid="home-boba-history-timeline"
      style={{
        background:
          "radial-gradient(800px circle at 85% -10%, hsla(330,80%,75%,0.3), transparent 55%)," +
          "radial-gradient(900px circle at 10% 110%, hsla(252,70%,70%,0.3), transparent 55%)," +
          "linear-gradient(180deg, hsl(260 55% 99%), hsl(330 45% 99%))",
      }}
    >
      <div className="absolute inset-0 bp-scatter opacity-50 pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-5xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-primary/15 text-xs font-medium text-primary uppercase tracking-[0.18em]">
            Untold
          </span>
          <h2 className="font-brand font-semibold text-[1.9rem] leading-tight sm:text-4xl lg:text-5xl tracking-tight mt-3">
            A brief (and <span className="italic">disputed</span>) history of boba
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto text-[15px] sm:text-base">
            How a chewy little accident in a Taiwanese teahouse became the drink the whole planet queues up for.
          </p>
        </div>

        <ol className="relative mt-10 md:mt-12 border-l-2 border-dashed border-primary/25 ml-3 pl-6 md:ml-0 md:mx-auto md:max-w-3xl md:pl-8">
          {TIMELINE.map((t, i) => (
            <li key={i} className="relative mb-8 md:mb-10" data-testid={`history-item-${i}`}>
              <span
                className="absolute -left-[29px] md:-left-[35px] top-1 w-4 h-4 md:w-5 md:h-5 rounded-full ring-4 ring-white"
                style={{
                  background: "radial-gradient(circle at 30% 30%, #fff 0%, #d8c9f5 55%, #5B3FD1 100%)",
                  boxShadow: "0 6px 14px -4px rgba(91,63,209,0.45)",
                }}
              />
              <div className="bp-card p-5 md:p-6 transition-transform hover:-translate-y-0.5">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-mono text-sm text-primary font-semibold">{t.year}</span>
                  <span className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground">
                    {t.place}
                  </span>
                </div>
                <h3 className="font-brand font-semibold text-lg sm:text-xl mt-1">{t.title}</h3>
                <p className="text-muted-foreground mt-2 leading-relaxed text-[15px] sm:text-base">{t.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div
          data-testid="home-did-you-know-callout"
          className="mt-6 rounded-3xl p-6 sm:p-8 md:p-10 relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, hsl(252 63% 54%), hsl(330 78% 60%))",
            color: "white",
          }}
        >
          <span
            className="absolute -top-6 -right-6 w-28 h-28 rounded-full opacity-30"
            style={{ background: "radial-gradient(circle at 30% 30%, #fff, transparent 70%)" }}
          />
          <p className="text-xs uppercase tracking-[0.18em] opacity-80">Did you know</p>
          <h3 className="font-brand font-semibold text-xl sm:text-2xl lg:text-3xl mt-2 max-w-3xl">
            The "bubble" in bubble tea isn't the pearls.
          </h3>
          <p className="opacity-90 mt-2 max-w-3xl text-[15px] sm:text-base">
            It's the frothy foam on top — the one that forms when you shake iced tea hard enough. The pearls are technically the <em>boba</em>. Which is also Taiwanese slang for… something the pearls kind of resemble. We'll let you Google it.
          </p>
        </div>
      </div>
    </section>
  );
}

export default memo(BobaHistory);
